import React, { useState, useRef, useEffect } from 'react';
import styled from '@emotion/styled';
import moment from 'moment';
import { DateRangePicker } from 'react-date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import AnalyticsInterval from '../../../../../shared/models/analytics/AnalyticsInterval';
import {
    color_shades_dark,
    color_shades_darker,
    color_shades_darkest,
    color_shades_light,
    color_shades_lighter,
    color_defaults_white,
    voicify_spark_darkest,
    voicify_spark_darker,
    voicify_spark_dark,
    voicify_spark_light,
    voicify_spark_lightest
} from '../../../../../shared/constants/colors';

const calendarIcon = `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%23ffffff' stroke-width='2'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' d='M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z' /%3E%3C/svg%3E`;
const chevronDownIcon = `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%23ffffff' stroke-width='2'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' d='M19 9l-7 7-7-7' /%3E%3C/svg%3E`;

interface DashboardDatePickerProps {
    startDate: string;
    endDate: string;
    interval: AnalyticsInterval;
    onChange: (startDate: string, endDate: string, interval: AnalyticsInterval) => void;
    isDisabled?: boolean;
}

type QuickSelectOption = 'today' | 'yesterday' | 'past7days' | 'thisWeek' | 'lastWeek' | 'thisMonth' | 'lastMonth' | 'thisYear' | 'custom';

const DATE_FORMAT = "YYYY-MM-DD";

const DashboardDatePicker: React.FC<DashboardDatePickerProps> = ({
    startDate,
    endDate,
    onChange,
    isDisabled = false
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [showCalendar, setShowCalendar] = useState(false);
    const [selectedOption, setSelectedOption] = useState<QuickSelectOption>('thisMonth');
    const [tempStartDate, setTempStartDate] = useState(startDate);
    const [tempEndDate, setTempEndDate] = useState(endDate);
    const [dropdownMaxHeight, setDropdownMaxHeight] = useState(400);
    const containerRef = useRef<HTMLDivElement>(null);

    // Sync temp dates when props change
    useEffect(() => {
        setTempStartDate(startDate);
        setTempEndDate(endDate);
    }, [startDate, endDate]);

    // Measure available space below the button when opening
    useEffect(() => {
        if (isOpen && containerRef.current) {
            const rect = containerRef.current.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom - 12;
            setDropdownMaxHeight(Math.max(200, spaceBelow));
        }
    }, [isOpen]);

    // Close on click outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    // Helper to safely parse a date string, returning a valid moment or a fallback
    const safeParseDate = (dateStr: string, fallback: moment.Moment): moment.Moment => {
        const parsed = moment(dateStr, DATE_FORMAT, true);
        if (parsed.isValid()) {
            return parsed;
        }
        // Try lenient parsing as a backup
        const lenientParsed = moment(dateStr);
        return lenientParsed.isValid() ? lenientParsed : fallback;
    };

    const formatDateRange = () => {
        const today = moment().startOf('day');
        const startOfMonth = moment().startOf('month');

        const start = safeParseDate(startDate, startOfMonth);
        const end = safeParseDate(endDate, today);

        if (start.isSame(end, 'day')) {
            return start.format('MMM D, YYYY');
        }

        if (start.isSame(end, 'year')) {
            return `${start.format('MMM D')} - ${end.format('MMM D, YYYY')}`;
        }

        return `${start.format('MMM D, YYYY')} - ${end.format('MMM D, YYYY')}`;
    };

    const getQuickSelectDates = (option: QuickSelectOption): { start: moment.Moment; end: moment.Moment; interval: AnalyticsInterval } => {
        const now = moment();
        const today = now.clone().startOf('day');

        switch (option) {
            case 'today':
                return { start: today.clone(), end: today.clone(), interval: 'day' };
            case 'yesterday':
                return { start: now.clone().subtract(1, 'day').startOf('day'), end: now.clone().subtract(1, 'day').startOf('day'), interval: 'day' };
            case 'past7days':
                return { start: now.clone().subtract(6, 'days').startOf('day'), end: today.clone(), interval: 'past 7 days' };
            case 'thisWeek':
                // Week to date: start of week to today
                return { start: now.clone().startOf('week'), end: today.clone(), interval: 'week' };
            case 'lastWeek':
                return { start: now.clone().subtract(1, 'week').startOf('week'), end: now.clone().subtract(1, 'week').endOf('week'), interval: 'week' };
            case 'thisMonth':
                // Month to date: start of month to today
                return { start: now.clone().startOf('month'), end: today.clone(), interval: 'this month' };
            case 'lastMonth':
                return { start: now.clone().subtract(1, 'month').startOf('month'), end: now.clone().subtract(1, 'month').endOf('month'), interval: 'past month' };
            case 'thisYear':
                // Year to date: start of year to today
                return { start: now.clone().startOf('year'), end: today.clone(), interval: 'year' };
            default:
                return { start: safeParseDate(tempStartDate, now.clone().startOf('month')), end: safeParseDate(tempEndDate, today), interval: 'custom' };
        }
    };

    const handleQuickSelect = (option: QuickSelectOption) => {
        setSelectedOption(option);
        const { start, end, interval } = getQuickSelectDates(option);
        setTempStartDate(start.format(DATE_FORMAT));
        setTempEndDate(end.format(DATE_FORMAT));

        if (option !== 'custom') {
            onChange(start.format(DATE_FORMAT), end.format(DATE_FORMAT), interval);
            setIsOpen(false);
        }
    };

    const handleDateRangeChange = (ranges: any) => {
        const start = moment(ranges.selection?.startDate).startOf('day');
        const end = moment(ranges.selection?.endDate).endOf('day');

        setSelectedOption('custom');
        setTempStartDate(start.format(DATE_FORMAT));
        setTempEndDate(end.format(DATE_FORMAT));
    };

    const handleApply = () => {
        onChange(tempStartDate, tempEndDate, 'custom');
        setIsOpen(false);
    };

    const quickSelectOptions: { key: QuickSelectOption; label: string }[] = [
        { key: 'today', label: 'Today' },
        { key: 'yesterday', label: 'Yesterday' },
        { key: 'past7days', label: 'Past 7 Days' },
        { key: 'thisWeek', label: 'This Week' },
        { key: 'lastWeek', label: 'Last Week' },
        { key: 'thisMonth', label: 'This Month' },
        { key: 'lastMonth', label: 'Last Month' },
        { key: 'thisYear', label: 'This Year' },
    ];

    const selectionRange = {
        startDate: safeParseDate(tempStartDate, moment().startOf('month')).toDate(),
        endDate: safeParseDate(tempEndDate, moment().startOf('day')).toDate(),
        key: 'selection',
        color: voicify_spark_dark
    };

    // Minimum date is January 1, 2026 - no data available before this
    const minDate = new Date(2026, 0, 1);

    // Maximum date is today - don't allow future date selection
    // Using end of today to be inclusive of the current day across all timezones
    const maxDate = moment().endOf('day').toDate();

    return (
        <Container ref={containerRef}>
            <DateButton
                onClick={() => !isDisabled && setIsOpen(!isOpen)}
                isOpen={isOpen}
                disabled={isDisabled}
                $isDisabled={isDisabled}
            >
                <CalendarIcon $isDisabled={isDisabled} />
                <DateText>{formatDateRange()}</DateText>
                {isDisabled ? (
                    <LoadingIndicator>
                        <LoadingDot style={{ animationDelay: '0ms' }} />
                        <LoadingDot style={{ animationDelay: '150ms' }} />
                        <LoadingDot style={{ animationDelay: '300ms' }} />
                    </LoadingIndicator>
                ) : (
                    <ChevronIcon isOpen={isOpen} />
                )}
            </DateButton>

            {isOpen && (
                <DropdownPanel $maxHeight={dropdownMaxHeight}>
                    <PanelContent>
                        {!showCalendar ? (
                            <>
                                <QuickSelectSection>
                                    <SectionTitle>Quick Select</SectionTitle>
                                    <QuickSelectGrid>
                                        {quickSelectOptions.map(option => (
                                            <QuickSelectButton
                                                key={option.key}
                                                isSelected={selectedOption === option.key}
                                                onClick={() => handleQuickSelect(option.key)}
                                            >
                                                {option.label}
                                            </QuickSelectButton>
                                        ))}
                                    </QuickSelectGrid>
                                </QuickSelectSection>

                                <Divider />

                                <ToggleSectionButton
                                    onClick={() => setShowCalendar(true)}
                                >
                                    <ToggleButtonLeft>
                                        <ToggleButtonLabel>Custom Range</ToggleButtonLabel>
                                        <SelectedRangeDisplay>
                                            {safeParseDate(tempStartDate, moment().startOf('month')).format('MMM D')} → {safeParseDate(tempEndDate, moment().startOf('day')).format('MMM D, YYYY')}
                                        </SelectedRangeDisplay>
                                    </ToggleButtonLeft>
                                    <ToggleButtonChevron isExpanded={false} />
                                </ToggleSectionButton>
                            </>
                        ) : (
                            <>
                                <ToggleSectionButton
                                    onClick={() => setShowCalendar(false)}
                                >
                                    <ToggleButtonLeft>
                                        <ToggleButtonLabel>Quick Select</ToggleButtonLabel>
                                    </ToggleButtonLeft>
                                    <ToggleButtonChevron isExpanded={true} />
                                </ToggleSectionButton>

                                <Divider />

                                <CalendarSection>
                                    <CalendarHeader>
                                        <SectionTitle>Select Date Range</SectionTitle>
                                        <SelectedRangeDisplay>
                                            {safeParseDate(tempStartDate, moment().startOf('month')).format('MMM D')} → {safeParseDate(tempEndDate, moment().startOf('day')).format('MMM D, YYYY')}
                                        </SelectedRangeDisplay>
                                    </CalendarHeader>
                                    <CalendarWrapper>
                                        <DateRangePicker
                                            ranges={[selectionRange]}
                                            onChange={handleDateRangeChange}
                                            months={1}
                                            direction="horizontal"
                                            rangeColors={[voicify_spark_light]}
                                            minDate={minDate as any}
                                            maxDate={maxDate as any}
                                        />
                                    </CalendarWrapper>
                                </CalendarSection>
                            </>
                        )}
                    </PanelContent>

                    <PanelFooter>
                        <CancelButton onClick={() => setIsOpen(false)}>
                            Cancel
                        </CancelButton>
                        <ApplyButton onClick={handleApply}>
                            Apply Range
                        </ApplyButton>
                    </PanelFooter>
                </DropdownPanel>
            )}
        </Container>
    );
};

// Styled Components
const Container = styled.div`
    position: relative;
    width: 100%;
`;

const DateButton = styled.button<{ isOpen: boolean; $isDisabled?: boolean }>`
    width: 100%;
    padding: 14px 16px;
    font-family: inherit;
    background: ${props => props.$isDisabled ? color_shades_lighter : props.isOpen ? voicify_spark_lightest : color_defaults_white};
    border-radius: 12px;
    border: 1px solid ${props => props.$isDisabled ? color_shades_light : props.isOpen ? voicify_spark_light : color_shades_light};
    color: ${props => props.$isDisabled ? color_shades_dark : voicify_spark_darkest};
    font-size: 15px;
    font-weight: 600;
    cursor: ${props => props.$isDisabled ? 'not-allowed' : 'pointer'};
    transition: all 0.2s ease;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
    display: flex;
    align-items: center;
    gap: 12px;
    opacity: ${props => props.$isDisabled ? 0.7 : 1};
    min-height: 48px;

    &:hover {
        background: ${props => props.$isDisabled ? color_shades_lighter : voicify_spark_lightest};
        border-color: ${props => props.$isDisabled ? color_shades_light : voicify_spark_light};
        box-shadow: ${props => props.$isDisabled ? '0 1px 3px rgba(0, 0, 0, 0.08)' : '0 2px 6px rgba(10, 95, 138, 0.12)'};
    }

    &:active {
        transform: translateY(0);
        box-shadow: 0 1px 3px rgba(10, 95, 138, 0.1);
    }

    /* Mac 13" small + Chromebook — reduce oversized date button font */
    @media (min-width: 1024px) and (max-width: 1439.98px) {
        font-size: 12px;
        padding: 10px 14px;
        min-height: 40px;
        gap: 10px;
    }

    @media (max-width: 400px) {
        padding: 12px 14px;
        font-size: 14px;
        gap: 10px;
    }
`;

const CalendarIcon = styled.div<{ $isDisabled?: boolean }>`
    width: 18px;
    height: 18px;
    background-image: url("${calendarIcon}");
    background-size: contain;
    background-repeat: no-repeat;
    flex-shrink: 0;
    filter: ${props => props.$isDisabled ? 'grayscale(100%) brightness(150%)' : 'invert(30%) sepia(90%) saturate(600%) hue-rotate(170deg) brightness(85%)'};
    opacity: ${props => props.$isDisabled ? 0.5 : 0.9};
`;

const DateText = styled.span`
    flex: 1;
    text-align: left;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
`;

const ChevronIcon = styled.div<{ isOpen: boolean }>`
    width: 16px;
    height: 16px;
    background-image: url("${chevronDownIcon}");
    background-size: contain;
    background-repeat: no-repeat;
    flex-shrink: 0;
    transition: transform 0.2s ease;
    transform: ${props => props.isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
    filter: invert(30%) sepia(90%) saturate(600%) hue-rotate(170deg) brightness(85%);
    opacity: 0.7;
`;

const LoadingIndicator = styled.div`
    display: flex;
    align-items: center;
    gap: 3px;
    padding: 0 4px;
`;

const LoadingDot = styled.div`
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background-color: ${color_shades_dark};
    animation: loadingPulse 0.8s ease-in-out infinite;

    @keyframes loadingPulse {
        0%, 80%, 100% {
            opacity: 0.3;
            transform: scale(0.8);
        }
        40% {
            opacity: 1;
            transform: scale(1);
        }
    }
`;

const DropdownPanel = styled.div<{ $maxHeight: number }>`
    position: absolute;
    top: calc(100% + 8px);
    left: 0;
    right: 0;
    background: ${color_defaults_white};
    border-radius: 16px;
    box-shadow:
        0 10px 40px rgba(0, 0, 0, 0.15),
        0 2px 10px rgba(0, 0, 0, 0.1);
    z-index: 1000;
    overflow: hidden;
    animation: slideDown 0.2s ease;
    max-height: ${props => props.$maxHeight}px;
    display: flex;
    flex-direction: column;

    /* Mac 13" small (1280–1439) */
    @media (min-width: 1280px) and (max-width: 1439.98px) {
        top: calc(100% + 4px);
    }

    /* Chromebook (1024–1279) */
    @media (min-width: 1024px) and (max-width: 1279.98px) {
        top: calc(100% + 4px);
    }

    @media (max-width: 400px) {
        border-radius: 12px;
        left: -8px;
        right: -8px;
    }

    @keyframes slideDown {
        from {
            opacity: 0;
            transform: translateY(-8px);
        }
        to {
            opacity: 1;
            transform: translateY(0);
        }
    }
`;

const PanelContent = styled.div`
    padding: 10px 8px 6px;
    background: ${color_defaults_white};
    border-radius: 16px 16px 0 0;
    overflow-y: auto;
    overflow-x: hidden;
    flex: 1;
    min-height: 0;

    @media (min-width: 1280px) and (max-width: 1439.98px) {
        padding: 8px 6px 4px;
    }

    @media (min-width: 1024px) and (max-width: 1279.98px) {
        padding: 6px 4px 3px;
    }

    @media (max-width: 400px) {
        padding: 12px 12px 6px;
        border-radius: 12px 12px 0 0;
    }
`;

const QuickSelectSection = styled.div`
    margin-bottom: 8px;

    @media (min-width: 1280px) and (max-width: 1439.98px) {
        margin-bottom: 6px;
    }

    @media (min-width: 1024px) and (max-width: 1279.98px) {
        margin-bottom: 4px;
    }
`;

const SectionTitle = styled.h4`
    font-size: 10px;
    font-weight: 700;
    font-family: inherit;
    color: ${color_shades_darker};
    text-transform: uppercase;
    letter-spacing: 0.8px;
    margin: 0 0 10px 0;

    @media (min-width: 1280px) and (max-width: 1439.98px) {
        margin: 0 0 6px 0;
    }

    @media (min-width: 1024px) and (max-width: 1279.98px) {
        margin: 0 0 4px 0;
    }
`;

const QuickSelectGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 6px;

    @media (min-width: 1280px) and (max-width: 1439.98px) {
        gap: 4px;
    }

    @media (min-width: 1024px) and (max-width: 1279.98px) {
        gap: 3px;
    }

    @media (max-width: 300px) {
        grid-template-columns: 1fr;
    }
`;

const QuickSelectButton = styled.button<{ isSelected: boolean }>`
    padding: 8px 10px;
    font-family: inherit;
    background: ${props => props.isSelected ? voicify_spark_dark : color_shades_lighter};
    color: ${props => props.isSelected ? color_defaults_white : color_shades_darkest};
    border: 1px solid ${props => props.isSelected ? voicify_spark_dark : color_shades_light};
    border-radius: 6px;
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.15s ease;
    text-align: center;

    &:hover {
        background: ${props => props.isSelected ? voicify_spark_darker : voicify_spark_lightest};
        border-color: ${props => props.isSelected ? voicify_spark_darker : voicify_spark_light};
        transform: translateY(-1px);
    }

    @media (min-width: 1280px) and (max-width: 1439.98px) {
        font-size: 11px;
        padding: 6px 8px;
    }

    @media (min-width: 1024px) and (max-width: 1279.98px) {
        font-size: 10px;
        padding: 5px 7px;
    }
`;

const Divider = styled.div`
    height: 1px;
    background: ${color_shades_light};
    margin: 8px 0;

    @media (min-width: 1280px) and (max-width: 1439.98px) {
        margin: 5px 0;
    }

    @media (min-width: 1024px) and (max-width: 1279.98px) {
        margin: 4px 0;
    }
`;

const CalendarSection = styled.div``;

const CalendarHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
    flex-wrap: wrap;
    gap: 8px;

    @media (min-width: 1280px) and (max-width: 1439.98px) {
        margin-bottom: 5px;
        gap: 5px;
    }

    @media (min-width: 1024px) and (max-width: 1279.98px) {
        margin-bottom: 4px;
        gap: 4px;
    }

    @media (max-width: 360px) {
        flex-direction: column;
        align-items: flex-start;
    }
`;

const ToggleSectionButton = styled.button`
    display: flex;
    justify-content: space-between;
    font-family: inherit;
    align-items: center;
    width: 100%;
    padding: 10px 12px;
    background: ${color_shades_lighter};
    color: ${color_shades_darkest};
    border: 1px solid ${color_shades_light};
    border-radius: 8px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.15s ease;
    min-height: 44px;

    &:hover {
        background: ${voicify_spark_lightest};
        border-color: ${voicify_spark_light};
    }

    @media (min-width: 1280px) and (max-width: 1439.98px) {
        font-size: 11px;
        padding: 8px 10px;
        min-height: 38px;
    }

    @media (min-width: 1024px) and (max-width: 1279.98px) {
        font-size: 10px;
        padding: 7px 9px;
        min-height: 34px;
    }

    @media (max-width: 360px) {
        padding: 8px 10px;
        font-size: 12px;
    }
`;

const ToggleButtonLeft = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
    flex: 1;
    min-width: 0;

    @media (max-width: 360px) {
        gap: 6px;
    }
`;

const ToggleButtonLabel = styled.span`
    color: ${color_shades_darker};
`;

const SelectedRangeDisplay = styled.span`
    font-size: 12px;
    font-weight: 600;
    color: ${voicify_spark_dark};
    background: ${color_defaults_white};
    padding: 4px 10px;
    border-radius: 4px;
    border: 1px solid ${voicify_spark_light};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;

    @media (min-width: 1280px) and (max-width: 1439.98px) {
        font-size: 10px;
        padding: 3px 8px;
    }

    @media (min-width: 1024px) and (max-width: 1279.98px) {
        font-size: 9px;
        padding: 2px 6px;
    }

    @media (max-width: 360px) {
        font-size: 11px;
        padding: 3px 8px;
    }
`;

const ToggleButtonChevron = styled.div<{ isExpanded: boolean }>`
    width: 14px;
    height: 14px;
    background-image: url("${chevronDownIcon}");
    background-size: contain;
    background-repeat: no-repeat;
    transition: transform 0.2s ease;
    transform: ${props => props.isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'};
    opacity: 0.6;
    filter: invert(40%);
`;

const CalendarWrapper = styled.div`
    overflow-x: hidden;
    overflow-y: visible;
    width: 100%;
    font-family: Muli, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;

    * {
        font-family: inherit;
    }

    /* Default desktop: zoom to fit within 380px sidebar */
    .rdrCalendarWrapper {
        zoom: 0.88;
    }

    /* Mac 13" small (1280–1439): zoom-out to fit 290px panel */
    @media (min-width: 1280px) and (max-width: 1439.98px) {
        .rdrCalendarWrapper {
            zoom: 0.76;
        }
    }

    /* Chromebook (1024–1279): strong zoom-out to fit 260px panel */
    @media (min-width: 1024px) and (max-width: 1279.98px) {
        .rdrCalendarWrapper {
            zoom: 0.62;
        }
    }

    @media (max-width: 360px) {
        .rdrCalendarWrapper {
            zoom: 0.85;
        }
    }

    .rdrMonthAndYearPickers {
        font-weight: 600;
        font-size: 13px;
    }

    .rdrNextPrevButton {
        background: ${color_shades_lighter};
        border-radius: 6px;
        width: 28px;
        height: 28px;
        min-width: 28px;
        
        &:hover {
            background: ${voicify_spark_lightest};
        }
    }

    .rdrDays {
        padding: 0;
    }

    .rdrDay {
        height: 28px;
        line-height: 28px;
    }

    .rdrDayNumber {
        top: 0;
        bottom: 0;
    }

    .rdrDayNumber span {
        color: ${color_shades_darkest};
        font-weight: 500;
        font-size: 12px;
    }

    .rdrDayPassive .rdrDayNumber span {
        color: ${color_shades_dark};
    }

    .rdrDayToday .rdrDayNumber span:after {
        background: ${voicify_spark_dark};
        bottom: 2px;
    }

    .rdrSelected, .rdrInRange, .rdrStartEdge, .rdrEndEdge {
        background: ${voicify_spark_light};
    }

    .rdrStartEdge, .rdrEndEdge {
        background: ${voicify_spark_dark};
    }

    .rdrDayStartPreview, .rdrDayInPreview, .rdrDayEndPreview {
        border-color: ${voicify_spark_dark};
    }

    .rdrDateDisplayWrapper {
        display: none;
    }

    .rdrDefinedRangesWrapper {
        display: none;
    }
`;

const PanelFooter = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    padding: 8px 12px;
    background: ${color_shades_lighter};
    border-top: 1px solid ${color_shades_light};
    flex-shrink: 0;

    @media (min-width: 1280px) and (max-width: 1439.98px) {
        padding: 5px 8px;
        gap: 6px;
    }

    @media (min-width: 1024px) and (max-width: 1279.98px) {
        padding: 4px 6px;
        gap: 5px;
    }

    @media (max-width: 360px) {
        padding: 10px 12px;
        gap: 8px;
    }
`;

const CancelButton = styled.button`
    padding: 8px 16px;
    font-family: inherit;
    background: ${color_defaults_white};
    color: ${color_shades_darker};
    border: 1px solid ${color_shades_light};
    border-radius: 6px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.15s ease;
    min-height: 36px;

    &:hover {
        background: ${color_shades_lighter};
        border-color: ${color_shades_dark};
    }

    @media (min-width: 1280px) and (max-width: 1439.98px) {
        font-size: 10px;
        padding: 8px 12px;
        min-height: 40px;
    }

    @media (min-width: 1024px) and (max-width: 1279.98px) {
        font-size: 9px;
        padding: 8px 10px;
        min-height: 40px;
    }

    @media (max-width: 360px) {
        padding: 6px 12px;
        font-size: 12px;
    }
`;

const ApplyButton = styled.button`
    padding: 8px 16px;
    font-family: inherit;
    background: ${voicify_spark_darkest};
    color: ${color_defaults_white};
    border: none;
    border-radius: 6px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.15s ease;
    box-shadow: 0 1px 3px rgba(10, 95, 138, 0.2);
    min-height: 36px;

    &:hover {
        background: ${voicify_spark_dark};
        box-shadow: 0 2px 6px rgba(10, 95, 138, 0.25);
    }

    &:active {
        transform: translateY(0);
    }

    @media (min-width: 1280px) and (max-width: 1439.98px) {
        font-size: 10px;
        padding: 8px 12px;
        min-height: 40px;
    }

    @media (min-width: 1024px) and (max-width: 1279.98px) {
        font-size: 9px;
        padding: 8px 10px;
        min-height: 40px;
    }

    @media (max-width: 360px) {
        padding: 6px 12px;
        font-size: 12px;
    }
`;

export default DashboardDatePicker;
